// Layer A of the two-layer save flow: friendly, user-facing "is every node
// filled in?" checks that run *before* serialization. Where Layer B (AJV) would
// surface a raw `minItems`/enum error, Layer A turns it into a Hebrew hint like
// "בחר ערך" and points at the offending node so the editor can highlight it.
//
// It also gates the Tier-3-deferred nodes (network/crm): they stay in the
// palette for discoverability, but a tree containing one cannot be saved (see
// the mapping-rebuild spec §3 / §8).
//
// Pure over the current React-Flow nodes + edges — no side effects. The
// optional allowed-variable list feeds the message-body token check (T6):
// an unknown {{token}} blocks the save, mirroring the send dialog.

import {
  channelKind,
  charLimit,
} from '@/components/notification-send/channelKinds'
import type { Edge, Node } from '@xyflow/react'
import type { UiNodeType } from '@/utils/treeSerializer'
import {
  findMalformedPlaceholders,
  findUnknownTokens,
} from '@/utils/templateTokens'
import { consumptionWindowProblem, type WindowProblem } from './consumptionKind'
import { calendarDraftProblems, type Calendar } from './calendar'
import {
  consumptionPeriod,
  hourConsumptionConflicts,
  streakUnit,
  type HourConsumptionConflict,
  type Period,
} from './hourlyAlert'

export type CompletenessIssue = { nodeId: string; message: string }

// Nodes whose mapping is deferred to Tier 3. They remain available in the
// sidebar but block a save until their mapping ships.
const DEFERRED_TYPES: ReadonlySet<UiNodeType> = new Set(['network', 'crm'])

const CONDITION_TYPES: ReadonlySet<UiNodeType> = new Set([
  'connection',
  'time',
  'consumption',
  'meter',
  'network',
])

const BRANCH_END_TYPES: ReadonlySet<UiNodeType> = new Set(['message', 'end'])
const BELOW_BRANCH_END =
  'לא ניתן לחבר רכיב מתחת לשליחה או לסיומת — מחקו את החיבור או את הרכיב'
const SEND_AND_END_TOGETHER =
  'שליחה וסיומת לא יכולות לצאת מאותו תנאי — השאירו אחת מהן'

const WINDOW_MESSAGES: Record<WindowProblem, string> = {
  'missing-start': 'בחר שעת התחלה',
  'missing-end': 'בחר שעת סיום',
  empty: 'שעת הסיום חייבת להיות אחרי שעת ההתחלה',
  'crosses-midnight': 'טווח השעות לא יכול לעבור את חצות',
}

const HOUR_CONSUMPTION_MESSAGES: Record<HourConsumptionConflict, string> = {
  kind: 'בדיקה שעתית נתמכת לצריכת מונה בלבד',
  percent: 'בדיקה שעתית לא תומכת באחוזים — בחר קוב׳ או ל׳',
  average: 'בדיקה שעתית לא תומכת בהשוואה לממוצע או לתקופה מקבילה',
}
const HOUR_STREAK_NOT_A_NUMBER = 'הזן מספר שעות (1 ומעלה)'
const HOUR_STREAK_AT_ROOT =
  'ספירת שעות ברציפות חייבת לבוא אחרי בדיקת צריכה שעתית'
const HOUR_STREAK_WITHOUT_HOUR_CHECK =
  'ספירת שעות ברציפות דורשת בדיקת צריכה שעתית מעליה'
const DAY_STREAK_AFTER_HOUR_CHECK =
  'ספירת ימים ברציפות לא יכולה לבוא אחרי בדיקת צריכה שעתית — עברו לשעות'
const TREE_HOURS_ON_DAY_CHECK =
  'לוח הזמנים של העץ כולל שעות, ובדיקה יומית לא יכולה להשתמש בו. הגדירו לבדיקה הזו ימים משלה.'
const TREE_CALENDAR_WITHOUT_TARGET = 'לוח הזמנים של העץ לא חל על אף בדיקת צריכה'

type Entry = { subject?: string; body?: string }

function channelEntry(content: unknown, key: string | undefined): Entry {
  if (!key || !content || typeof content !== 'object') return {}
  const entry = (content as Record<string, unknown>)[key]
  return entry && typeof entry === 'object' ? (entry as Entry) : {}
}

function entryTexts({ subject, body }: Entry): string[] {
  return [subject, body].filter((t): t is string => typeof t === 'string')
}

/** A channel of the site as the send node names it: by id, with the key its text is stored under. */
export type CompletenessChannel = {
  id: string
  key?: string
  category: string
  name: string
}

export function checkCompleteness(
  nodes: Node[],
  edges: Edge[],
  // Allowed {{variable}} names for message bodies (from the template-variable
  // registry). Defaults to none: a body with tokens cannot pass until the
  // caller supplies the real list — same fail-closed behavior as the dialog.
  allowedVariables: string[] = [],
  // Tree root. When given, nodes with no edge path from it are flagged as
  // orphans. Optional so callers that don't track a root keep the old
  // per-node-only behavior.
  rootId?: string,
  // Attributes whose values the author finds by searching consumers. They
  // get a message that names the search, not a pick.
  consumerAttributes: ReadonlySet<string> = new Set(),
  // Publish copies it into every consumption check without a calendar of its own.
  treeCalendar?: Calendar,
  // The site's channels, to read the ticked channels of a send node.
  channels: readonly CompletenessChannel[] = []
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = []
  const outgoingCount = new Map<string, number>()
  for (const e of edges) {
    outgoingCount.set(e.source, (outgoingCount.get(e.source) ?? 0) + 1)
  }

  // Reachability. Deleting a node orphans its children rather than cascading,
  // and `toSchemaTree` serializes by walking from the root — so an orphan is
  // not merely unsaved, it vanishes SILENTLY. Flagging it here is what turns
  // that silent data loss into a blocked save the operator can act on.
  if (rootId !== undefined) {
    const reachable = reachableFrom(nodes, edges, rootId)
    for (const n of nodes) {
      if (!reachable.has(n.id)) {
        issues.push({
          nodeId: n.id,
          message: 'רכיב מנותק מהעץ — חברו אותו לעץ או מחקו אותו',
        })
      }
    }
  }

  issues.push(...streakPlacementIssues(nodes, edges))

  // The send node and the end node end a branch. `toSchemaTree` reads nothing
  // below them, so a node hanging there would vanish on save; the editor no
  // longer offers the connector, but a loaded or older draft can carry the edge.
  const typeById = new Map(nodes.map(n => [n.id, n.type as UiNodeType]))
  const seenOutcome = new Map<string, UiNodeType>()
  for (const e of edges) {
    const from = typeById.get(e.source)
    const to = typeById.get(e.target)
    if (from && BRANCH_END_TYPES.has(from) && to) {
      issues.push({ nodeId: e.target, message: BELOW_BRANCH_END })
    }
    if (to && BRANCH_END_TYPES.has(to) && from && !BRANCH_END_TYPES.has(from)) {
      const other = seenOutcome.get(e.source)
      if (other && other !== to) {
        issues.push({ nodeId: e.target, message: SEND_AND_END_TOGETHER })
      }
      seenOutcome.set(e.source, to)
    }
  }

  for (const n of nodes) {
    const t = n.type as UiNodeType
    const d = (n.data ?? {}) as Record<string, unknown>

    if (DEFERRED_TYPES.has(t)) {
      issues.push({
        nodeId: n.id,
        message: 'רכיב זה טרם נתמך ולא ניתן לשמור התראה הכוללת אותו',
      })
      continue
    }

    switch (t) {
      case 'connection':
        if (!d.attributeName) {
          issues.push({ nodeId: n.id, message: 'בחר מאפיין' })
        } else if (!Array.isArray(d.values) || d.values.length === 0) {
          issues.push({
            nodeId: n.id,
            message: consumerAttributes.has(String(d.attributeName))
              ? 'חפש ובחר צרכן אחד לפחות'
              : 'בחר ערך אחד לפחות',
          })
        }
        break
      case 'time':
        if (streakUnit(d) === 'hours') {
          if (!isPositiveInt(d.value)) {
            issues.push({ nodeId: n.id, message: HOUR_STREAK_NOT_A_NUMBER })
          }
        } else if (!isPositiveInt(d.value)) {
          issues.push({ nodeId: n.id, message: 'הזן מספר ימים (1 ומעלה)' })
        }
        break
      case 'consumption':
        if (consumptionPeriod(d) === 'hour') {
          issues.push(...hourConsumptionIssues(n.id, d))
        } else if (!d.firstSelectedId) {
          issues.push({ nodeId: n.id, message: 'בחר סוג צריכה' })
        } else if (!d.secondSelectedId) {
          issues.push({ nodeId: n.id, message: 'בחר תנאי השוואה' })
        } else if (!isFiniteNumber(d.value)) {
          issues.push({ nodeId: n.id, message: 'הזן ערך סף' })
        } else if (
          String(d.thirdSelectedId ?? '2') === '1' &&
          !d.firstChecked &&
          !d.secondChecked
        ) {
          issues.push({
            nodeId: n.id,
            message: 'השוואה באחוזים דורשת סימון השוואה לממוצע או תקופה מקבילה',
          })
        } else if (d.firstChecked) {
          if (String(d.fourthSelectedId ?? '') !== '1') {
            issues.push({
              nodeId: n.id,
              message: 'השוואה למקסימום/מינימום טרם נתמכת — בחר ממוצע הצריכה',
            })
          } else if (String(d.fifthSelectedId ?? '') === '1') {
            issues.push({
              nodeId: n.id,
              message: 'תקופת שבועות טרם נתמכת — בחר חודשים',
            })
          } else if (
            String(d.fifthSelectedId ?? '') !== '2' ||
            !isPositiveInt(d.secondValue)
          ) {
            issues.push({
              nodeId: n.id,
              message: 'הזן מספר חודשים (1 ומעלה)',
            })
          }
        } else if (
          String(d.fourthSelectedId ?? '') !== '' ||
          String(d.fifthSelectedId ?? '') !== '' ||
          String(d.secondValue ?? '') !== ''
        ) {
          issues.push({
            nodeId: n.id,
            message: 'סמן "בהשוואה ל" כדי להחיל את שדות ההשוואה, או נקה אותם',
          })
        }
        if (consumptionPeriod(d) === 'day') {
          issues.push(...windowIssues(n.id, d), ...averageIssues(n.id, d))
        }
        issues.push(...calendarIssues(n.id, d, treeCalendar))
        break
      case 'meter':
        if (typeof d.code !== 'string' || d.code === '') {
          issues.push({ nodeId: n.id, message: 'בחר סוג התראת מד' })
        }
        break
      case 'message': {
        const ticked = Array.isArray(d.channelIds)
          ? d.channelIds.map(String)
          : []
        if (ticked.length === 0) {
          issues.push({ nodeId: n.id, message: 'בחר ערוץ שליחה אחד לפחות' })
        }
        // The send window's rules, for the ticked channels: a body in each,
        // none over its limit. A ticked id the list lacks cannot be read.
        const texts: string[] = []
        const common = typeof d.messageBody === 'string' ? d.messageBody : ''
        if (common.trim() !== '') texts.push(common)
        for (const id of ticked) {
          const channel = channels.find(c => c.id === id)
          if (!channel) continue
          const own = channelEntry(d.channelContent, channel.key)
          const body = channel.key ? (own.body ?? '') : common
          if (body.trim() === '') {
            issues.push({
              nodeId: n.id,
              message: `הזן טקסט להודעה בערוץ ${channel.name}`,
            })
          }
          const limit = charLimit(channelKind(channel))
          if (limit !== null && body.length > limit) {
            issues.push({
              nodeId: n.id,
              message: `הטקסט בערוץ ${channel.name} ארוך מ-${limit} תווים`,
            })
          }
          if (channel.key) texts.push(...entryTexts(own))
        }
        // Malformed first: a fragment the renderer grammar cannot parse is a
        // different failure (and copy) than a well-formed but unknown token.
        const malformed = [...new Set(texts.flatMap(findMalformedPlaceholders))]
        if (malformed.length > 0) {
          issues.push({
            nodeId: n.id,
            message: `תבנית משתנה שגויה: ${malformed.join(', ')}`,
          })
        }
        const unknown = [
          ...new Set(
            texts.flatMap(text => findUnknownTokens(text, allowedVariables))
          ),
        ]
        if (unknown.length > 0) {
          issues.push({
            nodeId: n.id,
            message: `משתנים לא מורשים: ${unknown.join(', ')}`,
          })
        }
        break
      }
    }

    // Structural: a (supported) condition node with no outgoing edge is a
    // dangling branch that never terminates in an outcome. Because the only
    // terminal nodes are actions (message/crm), requiring every condition node
    // to have a child guarantees every path ends in an outcome — the invariant
    // the compiler enforces with `assertConditionBranchesTerminate`.
    if (CONDITION_TYPES.has(t) && (outgoingCount.get(n.id) ?? 0) === 0) {
      issues.push({
        nodeId: n.id,
        message: 'חבר רכיב זה לפעולה או לתנאי המשך',
      })
    }
  }

  return issues
}

/**
 * Node ids reachable from `rootId` by following edges in the source→target
 * direction. Returns an empty set when the root itself is not among `nodes`,
 * which flags every node — the honest answer for a tree with no root.
 */
function reachableFrom(
  nodes: Node[],
  edges: Edge[],
  rootId: string
): Set<string> {
  const reachable = new Set<string>()
  if (!nodes.some(n => n.id === rootId)) return reachable

  const children = new Map<string, string[]>()
  for (const e of edges) {
    const existing = children.get(e.source)
    if (existing) existing.push(e.target)
    else children.set(e.source, [e.target])
  }

  const queue = [rootId]
  reachable.add(rootId)
  while (queue.length > 0) {
    const current = queue.shift() as string
    for (const child of children.get(current) ?? []) {
      // The `has` guard also makes an accidental cycle terminate.
      if (!reachable.has(child)) {
        reachable.add(child)
        queue.push(child)
      }
    }
  }
  return reachable
}

function hourConsumptionIssues(
  nodeId: string,
  d: Record<string, unknown>
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = []
  if (!d.secondSelectedId) {
    issues.push({ nodeId, message: 'בחר תנאי השוואה' })
  } else if (!isFiniteNumber(d.value)) {
    issues.push({ nodeId, message: 'הזן ערך סף' })
  }
  for (const conflict of hourConsumptionConflicts(d)) {
    issues.push({ nodeId, message: HOUR_CONSUMPTION_MESSAGES[conflict] })
  }
  return issues
}

// The engine counts a streak over the conditions above it that judge its own
// period or no period (filters). With only filters, publish accepts the tree
// but the streak counts the filter alone and opens at the first instant, so an
// hour streak needs an hour check above it, and a days node may not follow an
// hour check unless a day check is also above.
function streakPlacementIssues(
  nodes: Node[],
  edges: Edge[]
): CompletenessIssue[] {
  const byId = new Map(nodes.map(n => [n.id, n]))
  const parentOf = new Map(edges.map(e => [e.target, e.source]))
  const issues: CompletenessIssue[] = []
  for (const n of nodes) {
    if (n.type !== 'time') continue
    const own =
      streakUnit((n.data ?? {}) as Record<string, unknown>) === 'hours'
        ? 'hour'
        : 'day'
    const above: Array<Period | null> = []
    const seen = new Set([n.id])
    for (
      let id = parentOf.get(n.id);
      id !== undefined && !seen.has(id);
      id = parentOf.get(id)
    ) {
      seen.add(id)
      above.push(periodOf(byId.get(id)))
    }
    const hasOwn = above.some(p => p === own)
    if (own === 'hour' && above.length === 0) {
      issues.push({ nodeId: n.id, message: HOUR_STREAK_AT_ROOT })
    } else if (own === 'hour' && !hasOwn) {
      issues.push({ nodeId: n.id, message: HOUR_STREAK_WITHOUT_HOUR_CHECK })
    } else if (own === 'day' && !hasOwn && above.includes('hour')) {
      issues.push({ nodeId: n.id, message: DAY_STREAK_AFTER_HOUR_CHECK })
    }
  }
  return issues
}

function calendarIssues(
  nodeId: string,
  d: Record<string, unknown>,
  treeCalendar: Calendar | undefined
): CompletenessIssue[] {
  const own = d.calendar as Calendar | undefined
  const period = consumptionPeriod(d)
  if (own) {
    return calendarDraftProblems(own, period).map(p => ({
      nodeId,
      message: `לוח הזמנים של הבדיקה: ${p}`,
    }))
  }
  if (period === 'day' && treeCalendar?.some(w => w.hours !== undefined)) {
    return [{ nodeId, message: TREE_HOURS_ON_DAY_CHECK }]
  }
  return []
}

/**
 * What the tree calendar itself blocks, in Hebrew. Publish refuses a tree calendar that no consumption
 * check takes, and only the checks reachable from the root are saved.
 */
export function checkTreeCalendar(
  treeCalendar: Calendar | undefined,
  nodes: Node[],
  edges: Edge[],
  rootId: string
): string[] {
  if (!treeCalendar) return []
  const issues = calendarDraftProblems(treeCalendar, 'hour').map(
    p => `לוח הזמנים של העץ: ${p}`
  )
  const reachable = reachableFrom(nodes, edges, rootId)
  if (!nodes.some(n => n.type === 'consumption' && reachable.has(n.id))) {
    issues.push(TREE_CALENDAR_WITHOUT_TARGET)
  }
  return issues
}

function periodOf(node: Node | undefined): Period | null {
  const d = (node?.data ?? {}) as Record<string, unknown>
  if (node?.type === 'consumption') return consumptionPeriod(d)
  if (node?.type === 'time') return streakUnit(d) === 'hours' ? 'hour' : 'day'
  return null
}

function windowIssues(
  nodeId: string,
  d: Record<string, unknown>
): CompletenessIssue[] {
  const problem = consumptionWindowProblem(d)
  return problem ? [{ nodeId, message: WINDOW_MESSAGES[problem] }] : []
}

// The bounds of a percent threshold and the months of the equivalent period.
// An empty bound is no bound. An untouched months field is 1.
function averageIssues(
  nodeId: string,
  d: Record<string, unknown>
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = []
  if (String(d.thirdSelectedId ?? '2') === '1') {
    const given = [d.minValue, d.maxValue].filter(
      v => v !== undefined && String(v).trim() !== ''
    )
    if (given.some(v => !isFiniteNumber(v))) {
      issues.push({ nodeId, message: 'הגבול חייב להיות מספר' })
    } else if (given.length === 2 && Number(given[0]) > Number(given[1])) {
      issues.push({ nodeId, message: 'הגבול התחתון גדול מהעליון' })
    }
  }
  if (d.secondChecked && d.equivalentMonths !== undefined) {
    if (!isPositiveInt(d.equivalentMonths)) {
      issues.push({ nodeId, message: 'הזן מספר חודשים שלם (1 ומעלה)' })
    }
  }
  return issues
}

function isFiniteNumber(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'string' && value.trim() !== '') {
    return Number.isFinite(Number(value))
  }
  return false
}

function isPositiveInt(value: unknown): boolean {
  if (!isFiniteNumber(value)) return false
  const n = Number(value)
  return Number.isInteger(n) && n >= 1
}
